import { formatMinutesLabel } from 'src/pages/SettingsPage/tabs/RulesTab/components/RuleActiveHours'

export interface VoECallSchedulingValues {
  callSchedulingEnabled: boolean
  callWindowStartMinute: number
  callWindowEndMinute: number
  callRetryIntervalMinutes: number
  callMaxAttempts: number
}

/**
 * Defaults for a company with no saved config. The window opens at 10 AM
 */
export const DEFAULT_CALL_SCHEDULING: VoECallSchedulingValues = {
  callSchedulingEnabled: false,
  callWindowStartMinute: 600,
  callWindowEndMinute: 1020,
  callRetryIntervalMinutes: 240,
  callMaxAttempts: 3,
}

export const CALL_SCHEDULING_LIMITS = {
  minRetryIntervalMinutes: 15,
  maxRetryIntervalMinutes: 7 * 24 * 60,
  minMaxAttempts: 1,
  maxMaxAttempts: 10,
}

export const DEFAULT_CALL_TIMEZONE = 'America/New_York'

/** Short zone name for a timezone, e.g. "CDT". */
export const timezoneAbbreviation = (timezone?: string | null): string => {
  const zone = timezone || DEFAULT_CALL_TIMEZONE
  try {
    const part = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'short',
    })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName')
    return part?.value ?? zone
  } catch {
    return zone
  }
}

/** "4h", "30m", "1h 30m", "2d". */
export const formatInterval = (minutes: number): string => {
  if (minutes % 1440 === 0) return `${minutes / 1440}d`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}m`
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}

export const summarizeCallScheduling = (
  values: VoECallSchedulingValues
): string =>
  `Mon–Fri ${formatMinutesLabel(
    values.callWindowStartMinute
  )}–${formatMinutesLabel(
    values.callWindowEndMinute
  )} employer's local time · retry after ${formatInterval(
    values.callRetryIntervalMinutes
  )} · up to ${values.callMaxAttempts} attempt${
    values.callMaxAttempts === 1 ? '' : 's'
  }`

/** Returns a user-facing error for the first invalid field, or null. */
export const validateCallScheduling = (
  values: VoECallSchedulingValues
): string | null => {
  if (values.callWindowEndMinute <= values.callWindowStartMinute) {
    return 'End time must be after the start time'
  }
  if (
    values.callRetryIntervalMinutes <
      CALL_SCHEDULING_LIMITS.minRetryIntervalMinutes ||
    values.callRetryIntervalMinutes >
      CALL_SCHEDULING_LIMITS.maxRetryIntervalMinutes
  ) {
    return 'Retry interval must be between 15 minutes and 7 days'
  }
  if (
    values.callMaxAttempts < CALL_SCHEDULING_LIMITS.minMaxAttempts ||
    values.callMaxAttempts > CALL_SCHEDULING_LIMITS.maxMaxAttempts
  ) {
    return 'Max attempts must be between 1 and 10'
  }
  return null
}
